// Accounting › Invoices (Blueprint's alone; pages/ux-full accounting/invoices), on ui-web's ListPage
// (patterns/README.md, BL-189): every invoice a page at a time (invoices.list), by where it stands, the
// day it was issued, or a number or name; what the filter adds up to in the title block's figures. A row
// opens the invoice where it already opens (#/doc/invoice: its sheet on the customer's Money tab, a draft
// at Review); a draft isn't a link for someone who doesn't open Review (QA-716).
import { DataTable, Figures, invoiceColumns, money, TextField } from '@flowology/ui-web/fl';
import type { InvoiceListProps } from '@flowology/ui-web';
import { ListPage } from '@flowology/ui-web/patterns';
import { canSee, PageBanners, PLACES, usePageCrumb, type NavProps } from '../Nav.tsx';
import { docHref } from '../shared/doc-link.ts';
import { listStatus, moreRows, useListFilter, useMore } from './money-list-state.ts';
import { INVOICE_SHOWS, listInput, rangeWrong, type InvoiceShow } from './money-lists-model.ts';
import { BOOKS_PARTS } from './parts.ts';
import { useQuery } from './ui.tsx';

const invoiceHref = (id: string) => docHref({ kind: 'invoice', id });
const SHOWS = INVOICE_SHOWS.map(([id, label]) => ({ id, label }));
const PART = BOOKS_PARTS.find((p) => p.part === 'invoices')!;
const WRONG_DAYS = 'The first day is after the last.';

export function InvoicesPage({ nav, held }: { nav: NavProps; held: readonly string[] }) {
  // a draft is edited at Review on its job: a link only for those who open Review (QA-716)
  const opensDrafts = PLACES.some((p) => p.view === 'review' && canSee(p, held));
  const hrefOf = (r: { id: string; standing: string }) =>
    r.standing === 'draft' && !opensDrafts ? null : invoiceHref(r.id);
  const [f, set, typed, type] = useListFilter<InvoiceShow>();
  const wrong = rangeWrong(f);
  const q = useQuery<InvoiceListProps>('invoices.list', wrong ? null : listInput(f));
  const more = useMore<InvoiceListProps['rows'][number]>('invoices.list', q.data, (after) =>
    listInput(f, after),
  );
  const t = q.data?.totals;
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: PART.bp ?? PART.rail,
            lede: PART.lede,
            ...(t
              ? {
                  figures: (
                    <Figures
                      items={[
                        {
                          label: 'Invoices',
                          value: String(t.count),
                          sub: f.show === 'all' && !f.search ? 'every one' : 'in this filter',
                        },
                        { label: 'Still owed', value: money(t.owedCents) },
                        { label: 'Overdue', value: money(t.overdueCents) },
                      ]}
                    />
                  ),
                }
              : {}),
          }}
          search={{ label: 'Find a number or customer', value: typed, onChange: type, maxLength: 100 }}
          views={{
            label: 'Show',
            items: SHOWS,
            value: f.show,
            onChange: (show) => set({ show: show as InvoiceShow }),
          }}
          filters={
            <>
              <TextField
                type="date"
                width="date"
                label="Issued from"
                value={f.from}
                onChange={(from) => set({ from })}
              />
              <TextField
                type="date"
                width="date"
                label="to"
                value={f.to}
                onChange={(to) => set({ to })}
                error={wrong ? WRONG_DAYS : null}
              />
            </>
          }
          list={{
            title: 'Invoice list',
            ...(t ? { count: t.count } : {}),
            what: 'the invoices',
            status: listStatus(q.data, q.error),
            failedText: q.error ?? 'The invoices didn’t load.',
            onRetry: () => void q.reload(),
            children: q.data ? (
              <DataTable
                columns={invoiceColumns}
                rows={more.rows}
                hrefOf={hrefOf}
                more={moreRows(more, q.data.totals.count)}
                empty="No invoices here. Try another filter."
              />
            ) : null,
          }}
        />
      </main>
    </div>
  );
}
